import { useEffect, useMemo } from 'react'
import type { JSX } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { clsx } from 'clsx'

import {
  Button,
  CalendarIcon,
  ChannelAvatar,
  ChevronRightIcon,
  EmptyState,
  Link,
  PlusIcon,
  Skeleton,
  Text,
  useDelayedLoading,
  WarningIcon,
} from '@buffer-mono/popcorn'

import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { useChannelsConnected } from '~publish/contexts/AccountContext'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import type { UseComposerResult } from '~publish/hooks/usePostComposer'
import { useTimeFormatPreference } from '~publish/hooks/useTimeFormatPreference'
import { useTimezone } from '~publish/hooks/useTimezone'
import { PusherEvent } from '~publish/services/pusher'

import { useLastItemCount } from '../useLastItemCount'
import { useRealtimeRefetch } from '../useRealtimeRefetch'

import { formatUpNextTime } from './formatUpNextTime'
import { UP_NEXT_POSTS_LIMIT, useUpNextPosts } from './useUpNextPosts'
import type { UpNextPost } from './useUpNextPosts'

import { getSlotIds } from '~publish/helpers/getSlotIds'

import styles from './UpNextSection.module.css'
import { SectionHeader } from '../SectionHeader'

// The DRAFT_* events matter here too: buffer-web classifies a saved post by
// its NEW state, so a scheduled post saved back to a draft emits DRAFT_UPDATED
// (or DRAFT_MOVED via the legacy move flow) — without them the post lingers in
// Up Next. POST_FAILED covers scheduled → error leaving the list.
const UP_NEXT_REALTIME_EVENTS = [
  PusherEvent.POST_CREATED,
  PusherEvent.POST_UPDATED,
  PusherEvent.POST_SENT,
  PusherEvent.POST_FAILED,
  PusherEvent.POST_DELETED,
  PusherEvent.DRAFT_MOVED,
  PusherEvent.DRAFT_UPDATED,
  PusherEvent.DRAFT_APPROVED,
  PusherEvent.QUEUE_CHANGED,
  PusherEvent.POSTS_REORDERED,
]

// Post events here are user-driven (schedule / delete / reschedule) and low
// volume, so we use a short window. Comments has its own backfill-spam concern
// and stays on the 30s default.
const UP_NEXT_REFETCH_THROTTLE_MS = 3_000

export const UP_NEXT_LAST_COUNT_STORAGE_KEY = 'up-next-last-count'

const UP_NEXT_EDIT_CTA = 'publish-home-upNextCard-editPost-1'
const UP_NEXT_CREATE_CTA = 'publish-home-upNext-createPost-1'

export function UpNextSection(): JSX.Element {
  const { posts, totalCount, loading, error, refetch } =
    useUpNextPosts(UP_NEXT_POSTS_LIMIT)
  const hasPosts = posts.length > 0
  // The skeleton reveals after the CSS delay. The hook holds it mounted for a
  // minimum duration, so a response just after the reveal never cuts the fade
  // short. The full condition goes into the hook: the hold must survive the
  // moment posts arrive and hasPosts flips.
  const emptyWhileLoading = loading && !hasPosts
  const heldLoading = useDelayedLoading(emptyWhileLoading)
  const showSkeleton = emptyWhileLoading || heldLoading

  const [lastPostsCount, setLastPostsCount] = useLastItemCount(
    UP_NEXT_LAST_COUNT_STORAGE_KEY,
  )

  // Remember the loaded row count so the next page load can size the
  // loading skeleton to this section's real geometry.
  useEffect(() => {
    if (loading || error) return
    setLastPostsCount(posts.length)
  }, [loading, error, posts.length, setLastPostsCount])

  const { editPostInComposer, triggerAttributes } = usePostComposer()

  const channels = useChannelsConnected()
  const hasChannels = channels.length > 0
  const allowedChannelIds = useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  useRealtimeRefetch({
    events: UP_NEXT_REALTIME_EVENTS,
    refetch,
    allowedChannelIds,
    throttleMs: UP_NEXT_REFETCH_THROTTLE_MS,
  })

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <SectionHeader>Up Next</SectionHeader>
          {hasChannels && (
            <Text as="span" size="sm" color="subtle">
              <span className={styles.leadingSeparator}>· </span>
              {totalCount} {totalCount === 1 ? 'post' : 'posts'} scheduled
            </Text>
          )}
        </div>
        {hasChannels && (
          <Button
            as={RouterLink}
            to="/schedule"
            size="small"
            variant="tertiary"
            className={styles.seeAll}
            aria-label="View all scheduled posts"
          >
            View All
            <ChevronRightIcon />
          </Button>
        )}
      </div>

      {showSkeleton ? (
        <UpNextSkeleton rows={lastPostsCount} />
      ) : error && !hasPosts ? (
        <UpNextErrorState />
      ) : !hasPosts ? (
        <UpNextEmptyState showCreatePost={hasChannels} />
      ) : (
        <ul className={styles.list}>
          {posts.map((post) => (
            <UpNextPostRow
              post={post}
              key={post.id}
              triggerAttributes={triggerAttributes}
              editPostInComposer={editPostInComposer}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function UpNextSkeleton({ rows }: { rows: number | null }): JSX.Element {
  // Zero or unknown counts reserve the empty state's geometry: new accounts
  // resolve to the empty state, so that is the safest default footprint.
  const isEmptySized = rows === null || rows === 0
  const rowCount = isEmptySized ? 2 : Math.min(rows, UP_NEXT_POSTS_LIMIT)

  return (
    <div
      className={clsx(styles.list, isEmptySized && styles.listEmptySized)}
      // Skeleton visibility is announced as live status, not form output.
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="status"
      aria-label="Loading upcoming posts"
    >
      {getSlotIds('up-next-row-skeleton', rowCount).map((skeletonId) => (
        <Skeleton
          key={skeletonId}
          height={72}
          className={styles.skeletonRow}
          data-testid="up-next-skeleton"
        />
      ))}
    </div>
  )
}

function UpNextEmptyState({
  showCreatePost,
}: {
  showCreatePost: boolean
}): JSX.Element {
  return (
    <EmptyState size="small" className={styles.emptyState}>
      <EmptyState.Icon>
        <CalendarIcon />
      </EmptyState.Icon>
      <EmptyState.Description>
        No posts scheduled yet.
        <br />
        You&apos;ll see upcoming posts here.
      </EmptyState.Description>
      {showCreatePost && (
        <EmptyState.Actions>
          <NewPostComposerTrigger cta={UP_NEXT_CREATE_CTA}>
            <Button variant="secondary" size="small">
              <PlusIcon />
              Create Post
            </Button>
          </NewPostComposerTrigger>
        </EmptyState.Actions>
      )}
    </EmptyState>
  )
}

function UpNextErrorState(): JSX.Element {
  return (
    <EmptyState
      size="small"
      variant="warning"
      role="alert"
      className={styles.emptyState}
    >
      <EmptyState.Icon>
        <WarningIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>
        Couldn&apos;t load your upcoming posts
      </EmptyState.Heading>
      <EmptyState.Description>
        Please{' '}
        <Link as="button" onClick={() => window.location.reload()}>
          refresh this page
        </Link>
        .
      </EmptyState.Description>
    </EmptyState>
  )
}

type UpNextPostRowProps = {
  post: UpNextPost
  triggerAttributes: UseComposerResult['triggerAttributes']
  editPostInComposer: UseComposerResult['editPostInComposer']
}

function UpNextPostRow({
  post,
  triggerAttributes,
  editPostInComposer,
}: UpNextPostRowProps): JSX.Element {
  const timezone = useTimezone()
  const timeFormat = useTimeFormatPreference()

  const dueAtLabel = post.dueAt
    ? formatUpNextTime(post.dueAt, timezone, timeFormat)
    : ''

  const firstAsset = post.assets?.[0]
  const thumbnail = firstAsset?.thumbnail ?? firstAsset?.source ?? null
  const channelName = post.channel.name

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.rowButton}
        aria-label={
          dueAtLabel ? `Edit post scheduled ${dueAtLabel}` : 'Edit post'
        }
        {...triggerAttributes}
        onClick={() =>
          editPostInComposer({ postId: post.id, cta: UP_NEXT_EDIT_CTA })
        }
      >
        <span className={styles.rowHeader}>
          <ChannelAvatar
            size="small"
            alt={`${channelName} avatar`}
            src={post.channel.avatar ?? undefined}
            channel={post.channel.service}
            className={styles.avatar}
          />
          {dueAtLabel && (
            <Text size="sm" color="subtle" className={styles.time}>
              {dueAtLabel}
            </Text>
          )}
        </span>
        <span className={styles.rowBody}>
          {post.text ? (
            <Text className={styles.caption} translate="no">
              {post.text}
            </Text>
          ) : (
            <Text className={styles.caption}>
              Post on <span translate="no">{channelName}</span>
            </Text>
          )}
          {thumbnail && (
            <span className={styles.thumbnail}>
              <img
                src={thumbnail}
                alt=""
                loading="lazy"
                data-testid="up-next-thumbnail"
              />
            </span>
          )}
        </span>
      </button>
    </li>
  )
}
